/**
 * The headless adaptation session ("Anpassa text", ADR-0102 + addendum).
 *
 *   createAdaptationSession({ composition, registry, ctx, values, log })
 *
 * compiles the composition, replays the action log with live field
 * references, and returns everything a UI needs — the clean and tracked
 * documents, the per-entry report, change groups, conflicts, the form
 * (with the own fields under "Egna uppgifter"), the fields a reference chip
 * may point at, every addressable node with what it accepts — plus ACTION
 * BUILDERS that turn a user gesture into log entries.
 *
 * Pure: no I/O, no clock (pass `at`), no randomness. The host loop is
 *
 *   load log + values → session → render → user acts → builder → entries
 *   → persist (append) → load again → session …
 *
 * A builder never mutates the session; it returns entries (or a refusal)
 * that the host appends — server side, a host re-validates them
 * (`parseNewActionLogEntry`, `undoRefusal`, `fieldEntryRefusal`) and stamps
 * its own author / time.
 */
import { compileComposition } from '../document/compose.js';
import { describeCompositionForm } from '../document/form.js';
import { childLists, containsLocked, formatNodeAddress, isLockedNode, isTextTarget, logAnchorFromAddress } from '../patch/address.js';
import { assemblePatched } from '../patch/build.js';
import { stampBlockVersion } from '../patch/version.js';
import { customFieldKey, customFieldToFormField, customFieldUsage, customFieldValueFormat, fieldEntryRefusal, foldCustomFields, isValidCustomFieldKey, referencedKeys, validateCustomValues, } from '../patch/fields.js';
import { groupChanges, groupRevertTargets, undoRefusal } from '../patch/groups.js';
import { emptyActionLog, keepAsText as keepAsTextEntry, nextSeq, replayConflicts } from '../patch/log.js';
import { createReferenceResolver } from '../patch/references.js';
import { asProposal, listSuggestions, suggestionRefusal } from '../patch/suggestions.js';
import { parseTokenText, toTokenText } from '../patch/tokens.js';
import { CUSTOM_FIELDS_GROUP_ID, CUSTOM_FIELDS_GROUP_TITLE } from '../patch/types.js';
/** Slot types a reference chip may point at (scalar values). */
const REFERENCEABLE = new Set(['text', 'textarea', 'number', 'currency', 'date', 'boolean', 'select', 'select_with_freetext']);
const seqOf = (patchId) => (patchId && /^[1-9]\d*$/.test(patchId) ? Number(patchId) : null);
const splitParagraphs = (text) => text
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
const regionLists = (document) => document.parts.flatMap((p) => [p.letterhead ?? [], p.body, p.footer ?? []]);
export function createAdaptationSession(input) {
    const log = input.log ?? emptyActionLog();
    const values = { ...(input.composition.values ?? {}), ...(input.values ?? {}) };
    const composition = { ...input.composition, values };
    const meta = { author: input.author ?? 'local', at: input.at ?? input.ctx.now };
    const track = input.track !== false;
    const mode = input.mode ?? 'adapt';
    const proposals = input.proposals ?? (mode === 'suggest' ? 'overlay' : 'hide');
    // ---- Form + own fields ------------------------------------------------
    const baseForm = describeCompositionForm(composition, input.registry);
    const fold = foldCustomFields(log);
    const usage = customFieldUsage(log);
    const customDefs = [...fold.fields.values()];
    const customFormFields = customDefs.map(customFieldToFormField);
    const customGroup = {
        instanceId: CUSTOM_FIELDS_GROUP_ID,
        blockRef: '',
        blockKey: '',
        title: CUSTOM_FIELDS_GROUP_TITLE,
        summary: '',
        part: composition.parts?.[0]?.key ?? 'main',
        placement: 'body',
        fields: customFormFields,
        sharedKeys: [],
        fixed: {},
        custom: true,
    };
    const form = {
        ...baseForm,
        groups: customFormFields.length > 0 ? [...baseForm.groups, customGroup] : baseForm.groups,
        fields: [...baseForm.fields, ...customFormFields],
    };
    // ---- Compile + replay -------------------------------------------------
    const slotFields = {};
    for (const f of baseForm.fields)
        slotFields[f.key] = { type: f.type, ...(f.options ? { options: f.options } : {}) };
    const references = { values, fields: slotFields, format: input.ctx.format };
    const compiled = compileComposition(composition, input.registry, input.ctx);
    const base = compiled.document;
    const built = assemblePatched(base, log, { ...input.assemble, track, references, proposals }, compiled.diagnostics);
    const report = built.report ?? [];
    const groups = groupChanges(log, report);
    const suggestions = listSuggestions(log, report);
    const diagnostics = [...built.diagnostics, ...validateCustomValues(log, values)];
    const resolver = createReferenceResolver(base, { ...references, customFields: fold.fields });
    const groupTitle = new Map(baseForm.groups.map((g) => [g.instanceId, g.title]));
    const availableFields = [
        ...baseForm.fields
            .filter((f) => REFERENCEABLE.has(f.type) && !f.repeats)
            .map((f) => {
            const pv = resolver.preview(f.key);
            return {
                key: f.key,
                label: f.label,
                type: f.type,
                source: 'slot',
                group: groupTitle.get(f.groupId) ?? '',
                preview: pv?.text ?? null,
                ...(pv?.format ? { format: pv.format } : {}),
            };
        }),
        ...customDefs.map((f) => ({
            key: f.key,
            label: f.label,
            type: customFormFields.find((c) => c.key === f.key)?.type ?? 'text',
            source: 'custom',
            group: CUSTOM_FIELDS_GROUP_TITLE,
            preview: resolver.preview(f.key)?.text ?? null,
            format: customFieldValueFormat(f.type),
        })),
    ];
    const tokenFields = new Map(availableFields.map((f) => [f.key, { key: f.key, label: f.label, ...(f.format ? { format: f.format } : {}) }]));
    const preview = (key) => resolver.preview(key)?.text ?? null;
    const customFields = customDefs.map((f) => {
        const usedBy = usage.get(f.key) ?? [];
        return { ...f, usedBy, removable: usedBy.length === 0 };
    });
    // ---- Node index -------------------------------------------------------
    const nodes = [];
    const nodeObjects = new Map();
    const parts = [];
    const partObjects = new Map();
    const planes = new Map(base.manifest.map((m) => [m.instanceId, m.plane]));
    const undoTargetFor = (seq) => {
        if (undoRefusal(log, seq, report) == null)
            return seq;
        const head = groups.find((g) => g.seqs.includes(seq))?.headSeq;
        return head != null && undoRefusal(log, head, report) == null ? head : null;
    };
    const collectParts = (owner, ownerAddress, ctx) => {
        const plane = ctx.instanceId ? planes.get(ctx.instanceId) : undefined;
        const add = (kind, edit, object, extra = {}) => {
            const removed = edit?.change === 'removed';
            const inserted = edit?.change === 'inserted';
            const address = edit?.address ?? (inserted && edit?.patchId ? formatNodeAddress({ fromPatch: edit.patchId }) : undefined);
            if (!address && !removed)
                return;
            const anchor = !removed && address && ctx.editable ? logAnchorFromAddress(address) : null;
            const locked = ctx.locked || edit?.locked === true;
            const seq = edit?.change ? seqOf(edit.patchId) : null;
            const change = removed ? 'removed' : inserted ? 'inserted' : edit?.change === 'edited' ? 'adapted' : null;
            const fp = edit?.fingerprint;
            const can = anchor != null && fp != null;
            const row = kind === 'row';
            let restoreSeq = null;
            if (removed && seq != null)
                restoreSeq = undoRefusal(log, seq, report) == null ? seq : null;
            const part = {
                address: address ?? `ghost:${edit?.patchId ?? ''}`,
                anchor,
                ...(fp ? { fingerprint: fp } : {}),
                kind,
                ...(ownerAddress ? { ownerAddress } : {}),
                ...(ctx.instanceId ? { instanceId: ctx.instanceId } : {}),
                ...(plane ? { plane } : {}),
                origin: address?.startsWith('@') || inserted ? 'inserted' : 'base',
                change,
                seq,
                ...(change && edit?.layer ? { layer: edit.layer } : {}),
                ...(change && edit?.suggestion ? { suggestion: edit.suggestion } : {}),
                locked,
                ...extra,
                ops: {
                    editRow: row && can && !locked,
                    removeRow: row && can && !locked,
                    insertRowBefore: row && anchor != null && !ctx.locked,
                    insertRowAfter: row && anchor != null && !ctx.locked,
                    editLabel: !row && can && !locked && fp != null,
                },
                undoSeq: anchor != null && change != null && change !== 'removed' && seq != null ? undoTargetFor(seq) : null,
                restoreSeq,
            };
            parts.push(part);
            if (address)
                partObjects.set(part.address, { owner, object, kind });
        };
        if (owner.type === 'detailList') {
            for (const item of owner.items) {
                const plain = item.description.every((n) => n.type === 'paragraph');
                add('row', item.edit, item, {
                    text: toTokenText(item.term),
                    descriptionEditable: plain,
                    ...(plain ? { description: item.description.map((n) => (n.type === 'paragraph' ? toTokenText(n.content) : '')).join('\n\n') } : {}),
                });
            }
        }
        else if (owner.type === 'optionList') {
            for (const item of owner.items)
                add('option', item.edit, item, { text: toTokenText(item.content), ...((item.sourceKey ?? owner.sourceKey) ? { fieldKey: item.sourceKey ?? owner.sourceKey } : {}) });
        }
        else if (owner.type === 'table') {
            for (const column of owner.columns)
                if (column.header)
                    add('columnHeader', column.edit, column, { text: toTokenText(column.header) });
            if (owner.caption)
                add('caption', owner.captionEdit, owner, { text: toTokenText(owner.caption) });
        }
    };
    if (built.tracked) {
        const restoreShown = new Set();
        const seenInstances = new Set();
        const visit = (list, depth, ctx) => {
            for (const node of list) {
                const edit = node.edit;
                const instanceId = node.instanceId ?? ctx.instanceId;
                if (depth === 1 && node.instanceId && !seenInstances.has(node.instanceId)) {
                    seenInstances.add(node.instanceId);
                    const fp = edit?.instanceFingerprint;
                    const own = list.filter((n) => n.instanceId === node.instanceId);
                    const live = own.filter((n) => n.edit?.change !== 'removed');
                    const address = formatNodeAddress({ instanceId: node.instanceId });
                    const plane = planes.get(node.instanceId);
                    nodes.push({
                        address,
                        anchor: { instanceId: node.instanceId },
                        ...(fp ? { fingerprint: fp } : {}),
                        nodeType: 'instance',
                        instanceId: node.instanceId,
                        ...(plane ? { plane } : {}),
                        depth: 0,
                        origin: 'base',
                        change: live.length === 0 ? 'removed' : null,
                        seq: null,
                        locked: own.length > 0 && own.every((n) => n.edit?.locked === true || isLockedNode(n)),
                        lockNotice: false,
                        ops: {
                            editText: false,
                            remove: fp != null && live.length > 0 && !containsLocked(live),
                            insertBefore: fp != null,
                            insertAfter: fp != null,
                            append: false,
                            insertSection: fp != null,
                        },
                        undoSeq: null,
                        restoreSeq: null,
                    });
                }
                const removed = edit?.change === 'removed';
                const locked = edit?.locked === true || isLockedNode(node) || ctx.parentLocked;
                const lockNotice = locked && !ctx.parentLocked && !ctx.insideGhost && !removed;
                const address = edit?.address;
                const origin = address?.startsWith('@') || edit?.change === 'inserted' ? 'inserted' : 'base';
                let restoreSeq = null;
                if (removed && !ctx.insideGhost && edit?.patchId && !restoreShown.has(edit.patchId)) {
                    restoreShown.add(edit.patchId);
                    const s = seqOf(edit.patchId);
                    restoreSeq = s != null && undoRefusal(log, s, report) == null ? s : null;
                }
                const inserted = edit?.change === 'inserted';
                const nodeAddress = address ?? (inserted && edit?.patchId ? formatNodeAddress({ fromPatch: edit.patchId }) : undefined);
                if (nodeAddress || restoreSeq != null) {
                    const editable = !ctx.insideGhost && !removed && nodeAddress != null;
                    const container = node.type === 'section' || node.type === 'group' || node.type === 'notice';
                    const beside = editable && !ctx.parentLocked;
                    const anchor = editable && nodeAddress ? logAnchorFromAddress(nodeAddress) : null;
                    const changeSeq = edit?.change === 'edited' || inserted || removed ? seqOf(edit?.patchId) : edit?.movedBy ? seqOf(edit.movedBy) : null;
                    const change = removed ? 'removed' : inserted ? 'inserted' : edit?.change === 'edited' || edit?.movedBy ? 'adapted' : null;
                    const text = node.type === 'paragraph' || node.type === 'heading'
                        ? toTokenText(node.content)
                        : node.type === 'section' && node.title != null
                            ? node.title
                            : undefined;
                    const plane = instanceId ? planes.get(instanceId) : undefined;
                    const entry = {
                        address: nodeAddress ?? `ghost:${edit?.patchId ?? ''}`,
                        anchor,
                        ...(edit?.fingerprint ? { fingerprint: edit.fingerprint } : {}),
                        nodeType: node.type,
                        ...(instanceId ? { instanceId } : {}),
                        ...(plane ? { plane } : {}),
                        depth,
                        origin,
                        change,
                        seq: changeSeq,
                        ...(change && edit?.layer ? { layer: edit.layer } : {}),
                        ...(change && edit?.suggestion ? { suggestion: edit.suggestion } : {}),
                        locked,
                        lockNotice,
                        ...(text !== undefined ? { text } : {}),
                        ...(node.type === 'optionList' && node.sourceKey ? { fieldKey: node.sourceKey } : {}),
                        ...(node.type === 'detailList' ? { appendRow: editable && anchor != null && !locked } : {}),
                        ops: {
                            editText: editable && anchor != null && !locked && isTextTarget(node),
                            remove: editable && anchor != null && !locked && !containsLocked([node]) && (origin === 'inserted' || edit?.fingerprint != null),
                            insertBefore: beside && anchor != null && node.type !== 'signature',
                            insertAfter: beside && anchor != null && node.type !== 'signature',
                            append: editable && anchor != null && !locked && container,
                            insertSection: beside && anchor != null && node.type === 'section',
                        },
                        undoSeq: editable && change != null && change !== 'removed' && changeSeq != null ? undoTargetFor(changeSeq) : null,
                        restoreSeq,
                    };
                    nodes.push(entry);
                    if (nodeAddress)
                        nodeObjects.set(nodeAddress, node);
                    if (!removed && !ctx.insideGhost)
                        collectParts(node, nodeAddress, { locked, instanceId, editable });
                }
                for (const { list: child } of childLists(node)) {
                    const ghostRow = node.type === 'detailList' && node.items.some((i) => i.description === child && i.edit?.change === 'removed');
                    visit(child, depth + 1, { insideGhost: ctx.insideGhost || removed || ghostRow, parentLocked: locked, ...(instanceId ? { instanceId } : {}) });
                }
            }
        };
        for (const list of regionLists(built.tracked))
            visit(list, 1, { insideGhost: false, parentLocked: false });
    }
    const byAddress = new Map(nodes.map((n) => [n.address, n]));
    const partsByAddress = new Map(parts.map((p) => [p.address, p]));
    // ---- Builders ---------------------------------------------------------
    const ok = (entries, extra = {}) => ({
        ok: true,
        entries: entries.map((e) => stampBlockVersion(base, e)),
        diagnostics: extra.diagnostics ?? [],
        ...(extra.key ? { key: extra.key } : {}),
        ...(extra.detached ? { detached: extra.detached } : {}),
    });
    const refuse = (reason, extra = {}) => ({ ok: false, reason, ...extra });
    const parse = (text, original) => parseTokenText(text, { fields: tokenFields, ...(original ? { original } : {}), preview });
    /** Own-field references must be defined (a slot key outside the form is left to the replay). */
    const checkContent = (entry) => fieldEntryRefusal(log, entry);
    const undoEntry = (current, targetSeq, reason, rep) => {
        const refusal = undoRefusal(current, targetSeq, rep);
        if (refusal)
            return { refusal };
        return { entry: { op: 'undo', ...meta, payload: { targetSeq, reason: reason ?? 'undo' } } };
    };
    const reasonFor = (seq) => {
        const op = log.entries.find((e) => e.seq === seq)?.op;
        return op === 'removeNode' || op === 'removeRow' ? 'restore' : 'undo';
    };
    const labelContent = (hit) => {
        if (hit.kind === 'caption')
            return hit.owner.type === 'table' ? (hit.owner.caption ?? []) : [];
        if (hit.kind === 'option')
            return hit.object.content;
        if (hit.kind === 'columnHeader')
            return hit.object.header ?? [];
        return hit.object.term;
    };
    /** Token text of a row value → paragraphs (blank-line separated), each parsed against the old value. */
    const parseParagraphs = (text, original) => {
        const diagnostics = [];
        const detached = [];
        const runs = splitParagraphs(text).map((p) => {
            const out = parse(p, original);
            diagnostics.push(...out.diagnostics);
            return out.content;
        });
        const kept = new Set(runs.flatMap((r) => referencedKeys(r)));
        for (const key of original ? referencedKeys(original) : [])
            if (!kept.has(key) && !detached.includes(key))
                detached.push(key);
        return { runs, diagnostics, detached };
    };
    const actions = {
        editText(address, tokenText) {
            const node = byAddress.get(address);
            const object = nodeObjects.get(address);
            if (!node || !object || !node.anchor)
                return refuse('unknown_address');
            if (node.locked)
                return refuse('locked');
            if (!node.ops.editText || !node.fingerprint)
                return refuse('not_allowed');
            if (object.type === 'section') {
                const title = tokenText.replace(/\s+/g, ' ').trim();
                if (title === '')
                    return refuse('empty');
                if (title === (object.title ?? ''))
                    return refuse('unchanged');
                return ok([{ op: 'replaceText', ...meta, anchor: node.anchor, fingerprint: node.fingerprint, payload: { content: [{ type: 'text', text: title }] } }]);
            }
            if (object.type !== 'paragraph' && object.type !== 'heading')
                return refuse('not_allowed');
            const parsed = parse(tokenText, object.content);
            if (!parsed.changed)
                return refuse('unchanged', { diagnostics: parsed.diagnostics });
            if (parsed.content.length === 0)
                return refuse('empty', { diagnostics: parsed.diagnostics });
            const entry = {
                op: 'replaceText',
                ...meta,
                anchor: node.anchor,
                fingerprint: node.fingerprint,
                payload: { content: parsed.content },
            };
            const refusal = checkContent(entry);
            if (refusal)
                return refuse(refusal, { diagnostics: parsed.diagnostics });
            return ok([entry], { diagnostics: parsed.diagnostics, detached: parsed.detached });
        },
        remove(address) {
            const node = byAddress.get(address);
            if (!node || !node.anchor)
                return refuse('unknown_address');
            if (!node.ops.remove || !node.fingerprint)
                return refuse(node.locked ? 'locked' : 'not_allowed');
            return ok([{ op: 'removeNode', ...meta, anchor: node.anchor, fingerprint: node.fingerprint }]);
        },
        insertParagraph(address, position, tokenText) {
            const node = byAddress.get(address);
            if (!node || !node.anchor)
                return refuse('unknown_address');
            const allowed = position === 'append' ? node.ops.append : position === 'before' ? node.ops.insertBefore : node.ops.insertAfter;
            if (!allowed)
                return refuse(node.locked && position === 'append' ? 'locked' : 'not_allowed');
            const parsed = parse(tokenText.replace(/\r\n?/g, '\n').trim());
            if (parsed.content.length === 0)
                return refuse('empty', { diagnostics: parsed.diagnostics });
            const entry = { op: 'insertParagraph', ...meta, anchor: node.anchor, payload: { position, content: parsed.content } };
            const refusal = checkContent(entry);
            if (refusal)
                return refuse(refusal, { diagnostics: parsed.diagnostics });
            return ok([entry], { diagnostics: parsed.diagnostics });
        },
        insertSection(address, position, title, paragraphs) {
            const node = byAddress.get(address);
            if (!node || !node.anchor)
                return refuse('unknown_address');
            const allowed = position === 'append' ? node.ops.append : node.ops.insertSection;
            if (!allowed)
                return refuse(node.locked && position === 'append' ? 'locked' : 'not_allowed');
            const t = title.replace(/\s+/g, ' ').trim();
            if (t === '')
                return refuse('empty');
            const texts = typeof paragraphs === 'string' ? splitParagraphs(paragraphs) : paragraphs.map((p) => p.trim()).filter(Boolean);
            const diagnostics = [];
            const parsed = texts.map((p) => {
                const out = parse(p);
                diagnostics.push(...out.diagnostics);
                return out.content;
            });
            const entry = { op: 'insertClause', ...meta, anchor: node.anchor, payload: { position, title: t, paragraphs: parsed } };
            const refusal = checkContent(entry);
            if (refusal)
                return refuse(refusal, { diagnostics });
            return ok([entry], { diagnostics });
        },
        defineField(field) {
            const taken = new Set([...fold.claimedKeys, ...baseForm.fields.map((f) => f.key), ...Object.keys(values)]);
            const key = field.key ?? customFieldKey(field.label, taken);
            if (field.key != null && !isValidCustomFieldKey(field.key))
                return refuse('invalid_field');
            const entry = {
                op: 'defineField',
                ...meta,
                payload: { key, label: field.label.trim(), type: field.type, ...(field.help?.trim() ? { help: field.help.trim() } : {}) },
            };
            const refusal = fieldEntryRefusal(log, entry);
            return refusal ? refuse(refusal) : ok([entry], { key });
        },
        updateField(key, patch) {
            const payload = { key };
            if (patch.label !== undefined)
                payload.label = patch.label.trim();
            if (patch.type !== undefined)
                payload.type = patch.type;
            if (patch.help !== undefined)
                payload.help = patch.help == null || patch.help.trim() === '' ? null : patch.help.trim();
            const entry = { op: 'updateField', ...meta, payload };
            const refusal = fieldEntryRefusal(log, entry);
            return refusal ? refuse(refusal) : ok([entry]);
        },
        removeField(key) {
            const entry = { op: 'removeField', ...meta, payload: { key } };
            const refusal = fieldEntryRefusal(log, entry);
            if (refusal === 'field_in_use')
                return refuse(refusal, { usedBy: usage.get(key) ?? [] });
            return refusal ? refuse(refusal) : ok([entry]);
        },
        undo(seq, reason) {
            if (!log.entries.some((e) => e.seq === seq))
                return refuse('unknown_entry');
            const out = undoEntry(log, seq, reason ?? reasonFor(seq), report);
            if ('refusal' in out) {
                const key = fold.keyOf.get(seq);
                return refuse(out.refusal, out.refusal === 'field_in_use' && key ? { usedBy: usage.get(key) ?? [] } : {});
            }
            return ok([out.entry]);
        },
        undoGroupHead(groupId) {
            const group = groups.find((g) => g.groupId === groupId);
            if (!group)
                return refuse('unknown_group');
            if (group.headSeq == null)
                return refuse('already_undone');
            const out = undoEntry(log, group.headSeq, reasonFor(group.headSeq), report);
            return 'refusal' in out ? refuse(out.refusal) : ok([out.entry]);
        },
        revertGroup(groupId) {
            const group = groups.find((g) => g.groupId === groupId);
            if (!group)
                return refuse('unknown_group');
            const targets = groupRevertTargets(group);
            if (targets.length === 0)
                return refuse('already_undone');
            let current = log;
            const entries = [];
            for (const seq of targets) {
                const out = undoEntry(current, seq, reasonFor(seq), report);
                if ('refusal' in out)
                    return refuse(out.refusal);
                entries.push(out.entry);
                current = { ...current, entries: [...current.entries, { ...out.entry, seq: nextSeq(current) }] };
            }
            return ok(entries);
        },
        redo(groupId) {
            const group = groups.find((g) => g.groupId === groupId);
            if (!group)
                return refuse('unknown_group');
            if (group.redoSeq == null)
                return refuse('stale_redo');
            const out = undoEntry(log, group.redoSeq, 'undo', report);
            return 'refusal' in out ? refuse(out.refusal) : ok([out.entry]);
        },
        keepAsText(seq) {
            if (!log.entries.some((e) => e.seq === seq))
                return refuse('unknown_entry');
            const kept = keepAsTextEntry(log, seq, base, meta);
            if (!kept)
                return refuse('keep_as_text_unresolvable');
            const { seq: _s, ...entry } = kept.entry;
            return ok([entry]);
        },
        drop(seq) {
            if (!log.entries.some((e) => e.seq === seq))
                return refuse('unknown_entry');
            const out = undoEntry(log, seq, 'drop', report);
            return 'refusal' in out ? refuse(out.refusal) : ok([out.entry]);
        },
        editRow(address, termText, descriptionText) {
            const part = partsByAddress.get(address);
            const hit = partObjects.get(address);
            if (!part || !hit || !part.anchor || part.kind !== 'row')
                return refuse('unknown_address');
            if (part.locked)
                return refuse('locked');
            if (!part.ops.editRow || !part.fingerprint)
                return refuse('not_allowed');
            const item = hit.object;
            const term = parse(termText.replace(/\s+/g, ' ').trim(), item.term);
            const diagnostics = [...term.diagnostics];
            const detached = [...term.detached];
            const payload = {};
            if (term.changed)
                payload.term = term.content;
            if (descriptionText !== undefined) {
                if (!part.descriptionEditable)
                    return refuse('not_allowed');
                const original = item.description.flatMap((n) => (n.type === 'paragraph' ? n.content : []));
                const desc = parseParagraphs(descriptionText, original);
                diagnostics.push(...desc.diagnostics);
                const normalized = (descriptionText.replace(/\r\n?/g, '\n').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)).join('\n\n');
                if (normalized !== (part.description ?? '')) {
                    payload.description = desc.runs;
                    for (const k of desc.detached)
                        if (!detached.includes(k))
                            detached.push(k);
                }
            }
            if (payload.term === undefined && payload.description === undefined)
                return refuse('unchanged', { diagnostics });
            const entry = {
                op: 'replaceRow',
                ...meta,
                anchor: part.anchor,
                fingerprint: part.fingerprint,
                payload,
            };
            const refusal = checkContent(entry);
            if (refusal)
                return refuse(refusal, { diagnostics });
            return ok([entry], { diagnostics, ...(detached.length ? { detached } : {}) });
        },
        removeRow(address) {
            const part = partsByAddress.get(address);
            if (!part || !part.anchor || part.kind !== 'row')
                return refuse('unknown_address');
            if (!part.ops.removeRow || !part.fingerprint)
                return refuse(part.locked ? 'locked' : 'not_allowed');
            return ok([{ op: 'removeRow', ...meta, anchor: part.anchor, fingerprint: part.fingerprint }]);
        },
        insertRow(address, position, termText, descriptionText) {
            let anchor = null;
            if (position === 'append') {
                const node = byAddress.get(address);
                if (!node || !node.anchor || node.nodeType !== 'detailList')
                    return refuse('unknown_address');
                if (!node.appendRow)
                    return refuse(node.locked ? 'locked' : 'not_allowed');
                anchor = node.anchor;
            }
            else {
                const part = partsByAddress.get(address);
                if (!part || !part.anchor || part.kind !== 'row')
                    return refuse('unknown_address');
                if (!(position === 'before' ? part.ops.insertRowBefore : part.ops.insertRowAfter))
                    return refuse(part.locked ? 'locked' : 'not_allowed');
                anchor = part.anchor;
            }
            const term = parse(termText.replace(/\s+/g, ' ').trim());
            const desc = parseParagraphs(descriptionText);
            const diagnostics = [...term.diagnostics, ...desc.diagnostics];
            if (term.content.length === 0 && desc.runs.length === 0)
                return refuse('empty', { diagnostics });
            const entry = { op: 'insertRow', ...meta, anchor, payload: { position, term: term.content, description: desc.runs } };
            const refusal = checkContent(entry);
            if (refusal)
                return refuse(refusal, { diagnostics });
            return ok([entry], { diagnostics });
        },
        editLabel(address, tokenText) {
            const part = partsByAddress.get(address);
            const hit = partObjects.get(address);
            if (!part || !hit || !part.anchor || part.kind === 'row')
                return refuse('unknown_address');
            if (part.locked)
                return refuse('locked');
            if (!part.ops.editLabel || !part.fingerprint)
                return refuse('not_allowed');
            const parsed = parse(tokenText.replace(/\s+/g, ' ').trim(), labelContent(hit));
            if (!parsed.changed)
                return refuse('unchanged', { diagnostics: parsed.diagnostics });
            if (parsed.content.length === 0)
                return refuse('empty', { diagnostics: parsed.diagnostics });
            const entry = { op: 'replaceText', ...meta, anchor: part.anchor, fingerprint: part.fingerprint, payload: { content: parsed.content } };
            const refusal = checkContent(entry);
            if (refusal)
                return refuse(refusal, { diagnostics: parsed.diagnostics });
            return ok([entry], { diagnostics: parsed.diagnostics, ...(parsed.detached.length ? { detached: parsed.detached } : {}) });
        },
        acceptSuggestion(seq) {
            const refusal = suggestionRefusal(log, seq, 'accept', report);
            return refusal ? refuse(refusal) : ok([{ op: 'acceptSuggestion', ...meta, payload: { targetSeq: seq } }]);
        },
        rejectSuggestion(seq) {
            const refusal = suggestionRefusal(log, seq, 'reject', report);
            return refusal ? refuse(refusal) : ok([{ op: 'rejectSuggestion', ...meta, payload: { targetSeq: seq } }]);
        },
    };
    if (mode === 'suggest') {
        const contentBuilders = new Set([
            'editText',
            'remove',
            'insertParagraph',
            'insertSection',
            'editRow',
            'removeRow',
            'insertRow',
            'editLabel',
        ]);
        for (const name of Object.keys(actions)) {
            if (name === 'acceptSuggestion' || name === 'rejectSuggestion')
                continue;
            const build = actions[name];
            actions[name] = contentBuilders.has(name)
                ? (...args) => {
                    const out = build(...args);
                    return out.ok ? { ...out, entries: out.entries.map(asProposal) } : out;
                }
                : () => refuse('suggestions_only');
        }
    }
    return {
        log,
        lastSeq: nextSeq(log) - 1,
        base,
        document: built.document,
        ...(built.tracked ? { tracked: built.tracked } : {}),
        changes: built.changes ?? [],
        report,
        groups,
        conflicts: replayConflicts(report),
        detachedValues: built.detachedValues,
        deviatesFromStandard: built.deviatesFromStandard,
        deviatingInstances: built.deviatingInstances,
        diagnostics,
        ok: !diagnostics.some((d) => d.severity === 'error'),
        form,
        customFields,
        availableFields,
        nodes,
        nodeAt: (address) => byAddress.get(address),
        parts,
        partAt: (address) => partsByAddress.get(address),
        mode,
        suggestions,
        openSuggestions: suggestions.filter((x) => x.open),
        actions,
    };
}
//# sourceMappingURL=session.js.map